import 'twin.macro';
import Cookies from 'js-cookie';
import React from 'react';
import Button from 'components/Buttons/Button';
import { useTranslation } from 'react-i18next';
import tw from 'twin.macro';
import { DownloadProgrammation } from 'containers/Programmations/Programmation/Sidebar/DownloadProgrammation';
import FormGroup from 'components/Form/FormGroup';
import {
  useHideTitle,
  useProg,
} from 'containers/Programmations/atoms/progAtoms';
import { useNbOfMatieres } from 'containers/Programmations/atoms/matieresAtoms';
import {
  PrinterIcon,
  QueueListIcon,
  ServerStackIcon,
  SwatchIcon,
} from '@heroicons/react/24/outline';
import { useProgDisplayConfig } from 'containers/Programmations/ProgV2/atoms';
import ProgSendToCMSModal, {
  sendProgToCMSPayloadAtom,
} from 'containers/Programmations/Programmation/ProgSendToCMSModal';
import { useAtom } from 'jotai';
import { Field } from 'react-final-form';
import { FontSizeSelector } from 'components/FontSizeSelector';
import { PrintConfig } from '../types';
import { SidebarWithSections } from 'components/SubSidebar/SidebarWithSections';
import { FontsCoupleSelector } from 'components/Form/FontsCoupleSelector';
import { ColorSelector } from 'components/Form/ColorSelector';
import { BgImageSelectorWithImages } from 'components/Form/BgImageSelector/BgImageSelectorWithImages';
import { MarginsFieldWrapper } from 'components/Margins';
import { ElementsAndDisposition } from 'containers/Programmations/ProgV2/Preview/Sidebar/ElementsAndDisposition';
import { Checkbox } from 'components/Form/Checkboxes/Checkbox';

interface Props {
  matiereId?: string;
  printConfig: PrintConfig;
  onUpdatePrintConfig: (v: PrintConfig) => void;
  limitedBgSet: boolean;
}

export function Sidebar({
  matiereId,
  printConfig,
  onUpdatePrintConfig,
  limitedBgSet,
}: Props) {
  const { t } = useTranslation(['common', 'programmation']);
  const hideTitle = useHideTitle();
  const nbOfMatieres = useNbOfMatieres() || 0;
  const isMatiereEmpty = nbOfMatieres === 0;
  const prog = useProg();

  const displayConfig = useProgDisplayConfig();
  const [, setSendToCMSPayload] = useAtom(sendProgToCMSPayloadAtom);
  const queryParams = new URLSearchParams(location.search);
  const showSendToCms =
    queryParams.get('sharable') === 'true' || Cookies.get('teetshadmin');

  return (
    <>
      <ProgSendToCMSModal />
      <SidebarWithSections
        collapsedComponent={
          <DownloadProgrammation
            variant="collapsed"
            displayConfig={displayConfig}
            printConfig={printConfig}
            hideTitle={hideTitle}
          />
        }
      >
        <div tw="p-4">
          <div css={[isMatiereEmpty && tw`hidden`]}>
            <DownloadProgrammation
              displayConfig={displayConfig}
              printConfig={printConfig}
              hideTitle={hideTitle}
            />
            {showSendToCms && prog && (
              <Button
                fill
                large
                tw="mt-2"
                onClick={() =>
                  setSendToCMSPayload({
                    programmationId: prog.id,
                    programmationName: prog.name,
                    printConfig,
                  })
                }
                icon={ServerStackIcon}
              >
                {t('common:buttons.sendToCMS')}
              </Button>
            )}
          </div>
        </div>

        <SidebarWithSections.Section
          icon={QueueListIcon}
          title={t('common:pdf.elementsAndDisposition')}
        >
          <ElementsAndDisposition
            printConfig={printConfig}
            matiereId={matiereId}
            onUpdatePrintConfig={onUpdatePrintConfig}
          />
        </SidebarWithSections.Section>
        <SidebarWithSections.Section
          icon={SwatchIcon}
          title={t('common:pdf.appearance')}
        >
          <FormGroup label={t('common:pdf.fonts')}>
            <Field
              name="font"
              component={({ input: { onChange, value } }) => (
                <FontsCoupleSelector onChange={onChange} value={value} />
              )}
            />
          </FormGroup>
          <FormGroup label={t('common:pdf.fontSize')}>
            <Field
              name="size"
              render={({ input: { onChange, value } }) => (
                <FontSizeSelector onChange={onChange} value={value} />
              )}
            />
          </FormGroup>
          <FormGroup label={t('common:pdf.bgColor')}>
            <Field
              name="bgColor"
              render={({ input: { value, onChange } }) => (
                <ColorSelector light value={value} onChange={onChange} />
              )}
            />
          </FormGroup>
          <FormGroup label={t('common:pdf.bgImages')}>
            <Field
              name="bgColor"
              render={({ input: { value, onChange } }) => (
                <BgImageSelectorWithImages
                  limitedBgSet={limitedBgSet}
                  value={value}
                  onChange={onChange}
                />
              )}
            />
          </FormGroup>
        </SidebarWithSections.Section>
        <SidebarWithSections.Section
          icon={PrinterIcon}
          title={t('common:pdf.layout')}
        >
          <FormGroup label={t('common:pdf.margins')}>
            <MarginsFieldWrapper />
          </FormGroup>
          <Field
            name="withPagination"
            type="checkbox"
            render={({ input }) => (
              <Checkbox
                id="withPagination"
                label={t('common:pdf.withPagination')}
                description={t('common:pdf.onlyVisibleOnPdf')}
                {...input}
              />
            )}
          />
        </SidebarWithSections.Section>
      </SidebarWithSections>
    </>
  );
}
